import classNames from 'classnames';
import { IS_IOS_APP } from 'const';
import { useDialogContext } from 'context/DialogContext';
import React, { JSX, useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { useBannerContext } from '../../../context/BannerContext';
import { useSidebarLeftContext } from '../../../context/SidebarLeftContext';
import useRealm from '../../../hooks/realm/UseRealm';
import useIsMobileDevice from '../../../hooks/UseIsMobileDevice';
import Banner from '../../Banner';
import MobileMenu from '../../MobileMenu';
import Progress from '../../Progress';
import SidebarLeft from '../../SidebarLeft';
import SidebarRight from '../../SidebarRight';
import ResizableDiv from 'components/ResizeableDiv';
import useTheme from 'hooks/UseTheme';
import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';

/**
 * Wrap a route with some basic components
 */
export default function RouteWrapper(): JSX.Element {
  const mobileMenuRef = useRef<HTMLElement>(null);

  const { realm } = useRealm();
  const { secondaryPane, resizeable, isMobilePaneOpen, setIsMobilePaneOpen, mobileTriggerIcon } = useSidebarLeftContext();
  const { bannerIsVisible } = useBannerContext();
  const isMobileDevice = useIsMobileDevice();
  const { dialogsItems } = useDialogContext();
  const { isAgencyTheme, isStorroTheme } = useTheme();

  // We should only care for dialogItems that do not have the remainSizeOnSmallerScreens set (or false)
  // as those dialog are placed to to bottom of the screen
  const filteredDialogsItems = dialogsItems.filter(item => !item.remainSizeOnSmallerScreens);

  // Close the mobile pane when the secondaryPane is cleared
  useEffect(() => {
    if (!secondaryPane) {
      setIsMobilePaneOpen(false);
    }
  }, [secondaryPane, setIsMobilePaneOpen]);

  return (
    <>
      {/* show the mobile menu at the bottom */}
      {isMobileDevice && <MobileMenu mobileMenuRef={mobileMenuRef} />}

      <div className='flex'>
        <SidebarLeft />
        <div className='flex-1 min-w-0'>
          <Banner />
          <main
            // FIXME: REACT 19 - disable for now. Should fix Error: Cannot access refs during render
            // eslint-disable-next-line
            style={{
              // FIXME: REACT 19 - disable for now. Should fix Error: Cannot access refs during render
              // eslint-disable-next-line
              paddingBottom: isMobileDevice ? mobileMenuRef.current?.offsetHeight : 0,
            }}
            className={classNames('bg-white pt-safe h-screen flex transition-all duration-[350ms]', {
              'pt-16 md:pt-8': bannerIsVisible || (realm && realm.scheduledForAdminDemote), // make room for the top bar

              // if the modal is open, we create a nice animation just like Apple does on the iPhone
              // the background element create padding on the x-ass and to the top
              // so you have the feeling you the modal overlaps the background element in a layered way
              'rounded-md bg-white mt-3 overflow-hidden': isMobileDevice && filteredDialogsItems.length > 0,
              // for a real mobile device, we should also care about the safe-area to the top
              'mt-[var(--safe-area-inset-top,_env(safe-area-inset-top,_0px))]': IS_IOS_APP && filteredDialogsItems.length > 0,
            })}
          >
            {/* Mobile overlay pane — slides in from the left over the content */}
            {isMobileDevice && secondaryPane && (
              <>
                {/* Backdrop */}
                {isMobilePaneOpen && <div className='fixed inset-0 z-40 bg-black/30' onClick={() => setIsMobilePaneOpen(false)} />}

                {/* Sliding panel */}
                <div
                  className={classNames('fixed top-0 left-0 bottom-0 z-50 w-72 flex flex-col transition-transform duration-300', {
                    'bg-gray-50': isStorroTheme,
                    'bg-agency-background': isAgencyTheme,
                    'translate-x-0': isMobilePaneOpen,
                    '-translate-x-full': !isMobilePaneOpen,
                  })}
                >
                  {secondaryPane}
                </div>

                {/* Lip / tab toggle button */}
                <button
                  className={classNames(
                    'fixed top-1/3 z-50 -translate-y-1/2 transition-[left] duration-300',
                    'flex items-center justify-center w-5 h-12 rounded-r-lg shadow-md border border-l-0 border-gray-200',
                    {
                      'bg-gray-50': isStorroTheme,
                      'bg-agency-background': isAgencyTheme,
                      'left-72': isMobilePaneOpen,
                      'left-0': !isMobilePaneOpen,
                    },
                  )}
                  onClick={() => setIsMobilePaneOpen(prev => !prev)}
                >
                  {isMobilePaneOpen
                    ? (mobileTriggerIcon?.open ?? <CaretLeftIcon size={14} className='text-gray-500' />)
                    : (mobileTriggerIcon?.closed ?? <CaretRightIcon size={14} className='text-gray-500' />)}
                </button>
              </>
            )}

            {/* Desktop secondary pane */}
            {!isMobileDevice && secondaryPane && (
              <ResizableDiv
                enabled={resizeable}
                minWidth={250}
                maxWidth={500}
                className={classNames('shrink-0 grow-0 w-64 2xl:w-96 flex flex-col border-r', {
                  'bg-gray-50': isStorroTheme,
                  'bg-agency-background': isAgencyTheme,
                })}
              >
                {secondaryPane}
              </ResizableDiv>
            )}
            <div className='grow flex flex-col overflow-x-hidden'>
              <Outlet />
              <Progress mobileMenuRef={mobileMenuRef} />
            </div>
          </main>
          <SidebarRight mobileMenuRef={mobileMenuRef} />
        </div>
      </div>
    </>
  );
}
